import { FC } from 'react';
import { Tag } from '@carbon/react';
import {
  CheckmarkOutline,
  CircleDash,
  Close,
  Edit,
  InProgress,
} from '@carbon/react/icons';
import type { WellSectionStatus } from './types';
import styles from './well-workflow-status-badge.module.scss';

// Ported from the mock `WellWorkflowStatusBadge`. Two presentations:
//   • `pill`   (default) — Carbon Tag, used in section / detail-screen headers.
//   • `inline` — colored text + icon (the v2 mock `.layout2__status`), used in
//     the Well / Cost overview table rows. The mock never renders a pill inside
//     a table row, so the rows pass `variant="inline"`.

interface Props {
  status: WellSectionStatus;
  /** Surfaced alongside status — the `userEdited` flag is event-derived. */
  userEdited?: boolean;
  size?: 'sm' | 'md' | 'lg';
  /** `pill` for headers (default); `inline` for table rows (matches the mock). */
  variant?: 'pill' | 'inline';
}

type TagType = 'gray' | 'blue' | 'outline' | 'red' | 'teal';

const LABELS: Record<WellSectionStatus, string> = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  complete: 'Complete',
  removed: 'Removed',
  user_added: 'User Added',
};

const TYPES: Record<WellSectionStatus, TagType> = {
  not_started: 'gray',
  in_progress: 'outline',
  complete: 'blue',
  removed: 'red',
  user_added: 'teal',
};

const ICONS: Record<WellSectionStatus, React.ElementType> = {
  not_started: CircleDash,
  in_progress: InProgress,
  complete: CheckmarkOutline,
  removed: Close,
  user_added: CircleDash,
};

const INLINE_CLASS: Record<WellSectionStatus, string> = {
  not_started: styles.notStarted,
  in_progress: styles.inProgress,
  complete: styles.complete,
  removed: styles.removed,
  user_added: styles.userAdded,
};

export const WellWorkflowStatusBadge: FC<Props> = ({
  status,
  userEdited = false,
  size = 'sm',
  variant = 'pill',
}) => {
  const Icon = ICONS[status];

  if (variant === 'inline') {
    // The mock renders "Removed" as plain text with no icon; every other state
    // gets its icon. `userEdited` is intentionally not shown inline — it lives
    // in the section summary tiles as a count.
    return (
      <span className={`${styles.inline} ${INLINE_CLASS[status]}`}>
        <Icon size={16} />
        {LABELS[status]}
      </span>
    );
  }

  return (
    <span
      style={{ display: 'inline-flex', gap: '0.25rem', alignItems: 'center' }}
    >
      <Tag type={TYPES[status]} size={size} renderIcon={Icon}>
        {LABELS[status]}
      </Tag>
      {userEdited && status !== 'removed' && (
        <Tag type="cyan" size={size} renderIcon={Edit}>
          Edited
        </Tag>
      )}
    </span>
  );
};
